import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconCalendar from '../../assets/icons/calendar.svg?react'
import IconChevronDown from '../../assets/icons/chevron-down.svg?react'
import IconCheck from '../../assets/icons/check.svg?react'

export type PeriodOption = {
    id: string
    label: string
    range: string
}

type PeriodDropdownProps = {
    label: string
    selectedId: string
    isCustom: boolean
    options: PeriodOption[]
    from: string
    to: string
    select: (id: string) => void
    setFrom: (value: string) => void
    setTo: (value: string) => void
    onApply: () => void
}

export const PeriodDropdown: React.FC<PeriodDropdownProps> = ({
    label, selectedId, isCustom, options, from, to, select, setFrom, setTo, onApply
}) => {
    const { t } = useTranslation('dashboard')
    const [open, setOpen] = useState(false)

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`inline-flex items-center gap-2.5 h-10 px-3.5 rounded-xl bg-white shadow-sm border ${open ? 'border-neutralBlack-900' : 'border-neutralBlack-200'} hover:bg-neutralBlack-50`}
            >
                <IconCalendar className="w-3.5 h-3.5 text-neutralBlack-600" />
                <span className="text-[13.5px] font-medium text-neutralBlack-900 whitespace-nowrap">{label}</span>
                <IconChevronDown className={`w-2.5 h-2.5 text-neutralBlack-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <>
                    <button type="button" aria-label="close" className="fixed inset-0 z-40 cursor-default" onClick={() => setOpen(false)} />
                    <div className="absolute top-[46px] right-0 z-50 w-[252px] bg-white border border-neutralBlack-200 rounded-2xl shadow-lg p-1.5 flex flex-col gap-px">
                        {options.map((option) => (
                            <button
                                type="button"
                                key={option.id}
                                onClick={() => {
                                    select(option.id)
                                    if (option.id !== 'CUSTOM') setOpen(false)
                                }}
                                className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-[13.5px] text-neutralBlack-900 hover:bg-neutralBlack-100 ${option.id === selectedId ? 'bg-neutralBlack-50' : ''}`}
                            >
                                <IconCheck className={`w-2.5 h-2.5 flex-none text-neutralBlack-900 ${option.id === selectedId ? 'opacity-100' : 'opacity-0'}`} />
                                <span className="flex-1 min-w-0 text-left">{option.label}</span>
                                <span className="text-[11.5px] text-neutralBlack-500 whitespace-nowrap">{option.range}</span>
                            </button>
                        ))}

                        {isCustom && (
                            <div className="flex flex-col gap-2 mt-1 pt-3 px-2.5 pb-2 border-t border-neutralBlack-200">
                                <label className="flex flex-col gap-1">
                                    <span className="text-[11px] text-neutralBlack-500">{t('metrics.periodFrom')}</span>
                                    <input
                                        type="date"
                                        value={from}
                                        onChange={(e) => setFrom(e.target.value)}
                                        className="h-[38px] border border-neutralBlack-200 rounded-lg bg-white px-2.5 text-[13px] text-neutralBlack-900 outline-none focus:border-neutralBlack-900"
                                    />
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span className="text-[11px] text-neutralBlack-500">{t('metrics.periodTo')}</span>
                                    <input
                                        type="date"
                                        value={to}
                                        onChange={(e) => setTo(e.target.value)}
                                        className="h-[38px] border border-neutralBlack-200 rounded-lg bg-white px-2.5 text-[13px] text-neutralBlack-900 outline-none focus:border-neutralBlack-900"
                                    />
                                </label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onApply()
                                        setOpen(false)
                                    }}
                                    className="h-9 inline-flex items-center justify-center rounded-full bg-neutralBlack-900 text-white text-[13px] font-medium hover:bg-neutralBlack-800"
                                >
                                    {t('metrics.periodApply')}
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
